import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faBuilding } from "@fortawesome/pro-solid-svg-icons/faBuilding";
import { faCalendarAlt } from "@fortawesome/pro-solid-svg-icons/faCalendarAlt";
import { faHandshake } from "@fortawesome/pro-solid-svg-icons/faHandshake";
import { faRoute } from "@fortawesome/pro-solid-svg-icons/faRoute";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { useIntl } from "react-intl";

import { AnyEntity, Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { getPreviewEntityUniqueElementId } from "@mapmycustomers/shared/util/browser";

import { showEntityView } from "@app/store/entityView/actions";

import { AvgLevel } from "../../enums/AvgLevel";
import OverviewData from "../../store/OverviewData";
import BaseScreenProps from "../BaseScreenProps";
import MetricCard from "../MetricCard";

import { EntityStatCard } from "./components/previews/components/EntityListModal/EntityStatCard";
import { messages } from "./messages";
import styles from "./Overview.module.scss";
import { OnPreview } from "./types/onPreview";

interface Props {
  avgLevel: AvgLevel | undefined;
  overviewData: OverviewData;
  showEntityView: typeof showEntityView;
  similarLength: number;
}

const GrossMetrics: React.FC<Props & BaseScreenProps> = ({
  avgLevel,
  overviewData: { parentStatistics, statistics },
  selectedTeams,
  selectedUsers,
  showEntityView,
  similarLength,
}) => {
  const intl = useIntl();

  const similarAvgActivitiesCompleted = (parentStatistics.activitiesCompleted ?? 0) / similarLength;
  const similarAvgDealsCreated = (parentStatistics.dealsCreated ?? 0) / similarLength;
  const similarAvgAccountsCreated = (parentStatistics.accountsCreated ?? 0) / similarLength;
  const similarAvgContactsCreated = (parentStatistics.contactsCreated ?? 0) / similarLength;
  const similarAvgRoutesCreated = (parentStatistics.routesCreated ?? 0) / similarLength;

  const onPreview = useMemo<OnPreview>(
    () => ({
      onCompanyPreview: (companyId?: Company["id"]) =>
        showEntityView({ entityId: companyId, entityType: EntityType.COMPANY }),
      onDealPreview: (dealId?: Deal["id"]) =>
        showEntityView({ entityId: dealId, entityType: EntityType.DEAL }),
      onPersonPreview: (personId?: Person["id"]) =>
        showEntityView({ entityId: personId, entityType: EntityType.PERSON }),
    }),
    [showEntityView]
  );

  const activityRowEventHandlers = useCallback(
    (activity: AnyEntity) => ({
      onClick: ({ currentTarget }: React.MouseEvent<HTMLElement, MouseEvent>) => {
        showEntityView({
          elementId: getPreviewEntityUniqueElementId(currentTarget),
          entityId: activity.id,
          entityType: EntityType.ACTIVITY,
        });
      },
    }),
    [showEntityView]
  );

  const companyRowEventHandlers = useCallback(
    (company: AnyEntity) => ({
      onClick: () => showEntityView({ entityId: company.id, entityType: EntityType.COMPANY }),
    }),
    [showEntityView]
  );

  const dealRowEventHandlers = useCallback(
    (deal: AnyEntity) => ({
      onClick: () => showEntityView({ entityId: deal.id, entityType: EntityType.DEAL }),
    }),
    [showEntityView]
  );

  const personRowEventHandlers = useCallback(
    (person: AnyEntity) => ({
      onClick: () => showEntityView({ entityId: person.id, entityType: EntityType.PERSON }),
    }),
    [showEntityView]
  );

  return (
    <>
      <div className={styles.metricSection}>
        <EntityStatCard
          entityType={EntityType.ACTIVITY}
          onModalTableRow={activityRowEventHandlers}
          onPreview={onPreview}
          selectedTeams={selectedTeams}
          selectedUsers={selectedUsers}
        >
          {({ showModal }) => (
            <MetricCard
              average={
                avgLevel !== undefined &&
                intl.formatMessage(messages[avgLevel], {
                  average: intl.formatNumber(similarAvgActivitiesCompleted, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                  }),
                })
              }
              icon={faCalendarAlt}
              onClick={showModal}
              title={intl.formatMessage({
                id: "reports.overview.grossMetric.activitiesCompleted",
                defaultMessage: "Activities Done",
                description: "Card title for Activities Done on Overview Screen.",
              })}
              type="info"
              value={statistics.activitiesCompleted ?? 0}
            />
          )}
        </EntityStatCard>

        <EntityStatCard
          entityType={EntityType.DEAL}
          onModalTableRow={dealRowEventHandlers}
          onPreview={onPreview}
          selectedTeams={selectedTeams}
          selectedUsers={selectedUsers}
        >
          {({ showModal }) => (
            <MetricCard
              average={
                avgLevel !== undefined &&
                intl.formatMessage(messages[avgLevel], {
                  average: intl.formatNumber(similarAvgDealsCreated, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                  }),
                })
              }
              icon={faHandshake}
              onClick={showModal}
              title={intl.formatMessage({
                id: "reports.overview.grossMetric.dealsCreated",
                defaultMessage: "Deals Created",
                description: "Card title for Deals Created on Overview Screen.",
              })}
              type="info"
              value={statistics.dealsCreated ?? 0}
            />
          )}
        </EntityStatCard>

        <EntityStatCard
          entityType={EntityType.COMPANY}
          onModalTableRow={companyRowEventHandlers}
          onPreview={onPreview}
          selectedTeams={selectedTeams}
          selectedUsers={selectedUsers}
        >
          {({ showModal }) => (
            <MetricCard
              average={
                avgLevel !== undefined &&
                intl.formatMessage(messages[avgLevel], {
                  average: intl.formatNumber(similarAvgAccountsCreated, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                  }),
                })
              }
              icon={faBuilding}
              onClick={showModal}
              title={intl.formatMessage({
                id: "reports.overview.grossMetric.companiesCreated",
                defaultMessage: "Companies Created",
                description: "Card title for Companies Created on Overview Screen.",
              })}
              type="info"
              value={statistics.accountsCreated ?? 0}
            />
          )}
        </EntityStatCard>

        <EntityStatCard
          entityType={EntityType.PERSON}
          onModalTableRow={personRowEventHandlers}
          onPreview={onPreview}
          selectedTeams={selectedTeams}
          selectedUsers={selectedUsers}
        >
          {({ showModal }) => (
            <MetricCard
              average={
                avgLevel !== undefined &&
                intl.formatMessage(messages[avgLevel], {
                  average: intl.formatNumber(similarAvgContactsCreated, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                  }),
                })
              }
              icon={faUser}
              onClick={showModal}
              title={intl.formatMessage({
                id: "reports.overview.grossMetric.contactsCreated",
                defaultMessage: "Contacts Created",
                description: "Card title for Contacts Created on Overview Screen.",
              })}
              type="info"
              value={statistics.contactsCreated ?? 0}
            />
          )}
        </EntityStatCard>

        <EntityStatCard
          entityType={EntityType.ROUTE}
          onPreview={onPreview}
          selectedTeams={selectedTeams}
          selectedUsers={selectedUsers}
        >
          {({ showModal }) => (
            <MetricCard
              average={
                avgLevel !== undefined &&
                intl.formatMessage(messages[avgLevel], {
                  average: intl.formatNumber(similarAvgRoutesCreated, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                  }),
                })
              }
              icon={faRoute}
              onClick={showModal}
              title={intl.formatMessage({
                id: "reports.overview.grossMetric.routesCreated",
                defaultMessage: "Routes Created",
                description: "Card title for Routes Created on Overview Screen.",
              })}
              type="info"
              value={statistics.routesCreated ?? 0}
            />
          )}
        </EntityStatCard>
      </div>
    </>
  );
};

const mapDispatchToProps = {
  showEntityView,
};

export default connect(null, mapDispatchToProps)(GrossMetrics);
